import type {
  MachineAttachmentItemDto,
  MachineQuotationItemDto,
  QuotationDto,
  QuotationPriceOnlyDto
} from '@api/machines'
import {
  getIsNonEditableQuote,
  QuoteItemStockStatus,
  QuoteStatus,
  StockTypes
} from '@common/utils'
import { useMemo } from 'react'

export const useQuoteEquipmentStatus = (
  machine: MachineQuotationItemDto & MachineAttachmentItemDto,
  quotation: QuotationDto | undefined,
  quoteNewPrice: QuotationPriceOnlyDto,
  isNestedAttachment?: boolean
) => {
  const isNonEditableQuote = getIsNonEditableQuote(quotation?.status)
  const isQuoteAccepted = quotation?.status === QuoteStatus.ACCEPTED
  const isQuoteArchived = quotation?.status === QuoteStatus.ARCHIVED
  const machineNewPrice = quoteNewPrice?.machineItems?.find(
    item => item.quotationItemId === machine.quotationItemId
  )
  const attachmentNewPrice = quoteNewPrice?.machineAttachmentItems?.find(
    item => item.quotationItemId === machine.quotationItemId
  )

  const itemIsOutOfStock = useMemo(() => {
    const isSoldOut =
      !isQuoteAccepted &&
      (machine.stockInfo?.stockStatus === QuoteItemStockStatus.SOLD ||
        machine.stockInfo?.stockStatus ===
          QuoteItemStockStatus.ON_ORDER_BLOCKED ||
        machine.stockInfo?.stockStatus ===
          QuoteItemStockStatus.SOLD_PENDING_DELIVERY ||
        machine.stockInfo?.stockStatus === QuoteItemStockStatus.CUSTOMER_OWNED)

    if (!machine.stockInfo || machine.stockType === StockTypes.FACTORY_ORDER) {
      return false
    }

    return Boolean(isSoldOut)
  }, [machine, isQuoteAccepted])

  const itemIsConfirmed =
    isNonEditableQuote &&
    machine.stockInfo?.stockStatus === QuoteItemStockStatus.SOLD

  const isEqBrandArchived = machine.hasItsBrandArchived
  const isEqArchived = machine.isArchived

  const isMachineInStockOrOrdered =
    machine.stockType === StockTypes.IN_STOCK ||
    machine.stockType === StockTypes.ON_ORDER ||
    machine.machineAttachments?.some(
      attachment =>
        attachment.stockType === StockTypes.IN_STOCK ||
        attachment.stockType === StockTypes.ON_ORDER
    )

  const hasArchivedAttachments = machine.machineAttachments?.some(
    attachment => attachment.isArchived || attachment.hasItsBrandArchived
  )

  const itemsPriceUpdatesCount =
    (quoteNewPrice?.machineItems?.find(
      eq => eq.quotationItemId === machine.quotationItemId
    )?.optionItemsCount ?? 0) +
    (quoteNewPrice?.machineAttachmentItems?.find(
      eq => eq.quotationItemId === machine.quotationItemId
    )?.optionItemsCount ?? 0)

  const nestedAttachmentPriceUpdatesCount =
    quoteNewPrice?.machineAttachmentItems?.find(
      eq => eq.quotationItemId === machine.quotationItemId
    )?.optionItemsCount ?? 0

  const problematicItemsCount = isNestedAttachment
    ? (machine.incompleteCount ?? 0) + nestedAttachmentPriceUpdatesCount
    : (machine.incompleteCount ?? 0) + itemsPriceUpdatesCount

  const itemUsesLifeCycleCosting = useMemo(() => {
    if (machineNewPrice) {
      if (machineNewPrice.netCapitalizedCost) {
        return (
          machineNewPrice.netCapitalizedCost !==
          machine.stockInfo?.netCapitalizedCost
        )
      }
      return Boolean(machineNewPrice.netCapitalizedCost)
    }

    if (attachmentNewPrice) {
      if (attachmentNewPrice.netCapitalizedCost) {
        return (
          attachmentNewPrice.netCapitalizedCost !==
          machine.stockInfo?.netCapitalizedCost
        )
      }
      return Boolean(attachmentNewPrice?.netCapitalizedCost)
    }
    return false
  }, [machineNewPrice, attachmentNewPrice, machine.stockInfo])

  const itemLifeCycleCostChange = useMemo(() => {
    const priceState = {
      changed: false,
      decreased: false,
      increased: false
    }

    if (
      machineNewPrice?.netCapitalizedCost &&
      machine.stockInfo?.netCapitalizedCost
    ) {
      Object.assign(priceState, {
        changed:
          machineNewPrice.netCapitalizedCost !==
          machine.stockInfo.netCapitalizedCost,
        decreased:
          machineNewPrice.netCapitalizedCost <
          machine.stockInfo.netCapitalizedCost,
        increased:
          machineNewPrice.netCapitalizedCost >
          machine.stockInfo.netCapitalizedCost
      })
    }

    if (
      attachmentNewPrice?.netCapitalizedCost &&
      machine.stockInfo?.netCapitalizedCost
    ) {
      Object.assign(priceState, {
        changed:
          attachmentNewPrice.netCapitalizedCost !==
          machine.stockInfo.netCapitalizedCost,
        decreased:
          attachmentNewPrice.netCapitalizedCost <
          machine.stockInfo.netCapitalizedCost,
        increased:
          attachmentNewPrice.netCapitalizedCost >
          machine.stockInfo.netCapitalizedCost
      })
    }

    return priceState
  }, [machineNewPrice, attachmentNewPrice, machine.stockInfo])

  const itemUsesNetCapitalizedCosting = useMemo(() => {
    if (machineNewPrice) {
      return (
        Boolean(!machineNewPrice.netCapitalizedCost) &&
        Boolean(machine.stockInfo?.netCapitalizedCost)
      )
    }
    if (attachmentNewPrice) {
      return (
        Boolean(!attachmentNewPrice?.netCapitalizedCost) &&
        Boolean(machine.stockInfo?.netCapitalizedCost)
      )
    }
    return false
  }, [machineNewPrice, attachmentNewPrice, machine.stockInfo])

  const isSoldPendingDelivery =
    machine.stockInfo?.stockStatus ===
    QuoteItemStockStatus.SOLD_PENDING_DELIVERY

  const soldPendingDeliveryWithPriceChanges =
    isSoldPendingDelivery &&
    (itemUsesLifeCycleCosting ||
      itemLifeCycleCostChange.changed ||
      itemUsesNetCapitalizedCosting)

  return {
    isNonEditableQuote,
    itemIsOutOfStock,
    itemIsConfirmed,
    isQuoteArchived,
    isQuoteAccepted,
    isEqArchived,
    isEqBrandArchived,
    isMachineInStockOrOrdered,
    hasArchivedAttachments,
    itemUsesLifeCycleCosting,
    itemLifeCycleCostChange,
    itemUsesNetCapitalizedCosting,
    itemsPriceUpdatesCount,
    nestedAttachmentPriceUpdatesCount,
    soldPendingDeliveryWithPriceChanges,
    problematicItemsCount
  }
}
